import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import {
    Box,
    Stack,
    TextField,
    Divider,
    IconButton,
    Typography,
} from "@mui/material";
import EmployeePicker from "./EmployeePicker";
import ProjectPicker from "./ProjectPicker";
import QuotePicker from "./QuotePicker";
import ServicePicker from "./ServicePicker";
import HSEPicker from "./HSEPicker";
import ProjectTypeDropdown from "./ProjectTypeDropdown";
import CalendarDateIcon from "./Icons/CalendarDateIcon";
import IsoWeekIcon from "./Icons/IsoWeekIcon";
import NightsStayedIcon from "./Icons/NightsStayedIcon";
import TotalCostIcon from "./Icons/TotalCostIcon";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import WeekPicker from "./WeekPicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import moment from "moment-timezone";
import Transition from "./DialogTransistion";

const TZ = "America/New_York";

const parseDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    // Safari needs slashes instead of dashes for simple constructor usage
    const normalized =
        typeof value === "string" ? value.replace(/-/g, "/") : value;
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const calcNights = (start, end) => {
    if (!(start && end)) return 0;
    const startDate = moment(start).startOf("day");
    const endDate = moment(end).startOf("day");
    const delta = endDate.diff(startDate, "days");
    return Math.max(delta, 0);
};

export default function AddPerDiemForm(props) {
    const { user, token } = props;
    const { employees } = props;
    const { openPerDiem, setOpenPerDiem } = props;
    const { editing, expense } = props;
    const { employee, handleChangeEmployee } = props;
    const { createPerDiem, updatePerDiem } = props;
    const { isoWeek } = props;
    const { onIsoWeekChange } = props;
    const { perDiemRates = [] } = props;

    const [menuSelection, setMenuSelection] = React.useState(0);
    const menuOptions = ["Projects", "Services", "HSE's"];
    const [values, setValues] = React.useState({});
    const [errors, setErrors] = React.useState({
        project: null,
        start_date: null,
        end_date: null,
        rate: null,
        employee: null,
    });
    const [isValid, setIsValid] = React.useState(true);
    const didMount = React.useRef(false);

    // Function to check if selected employee (or user if no employee selected) has APUS group
    const selectedEmployeeHasApus = React.useMemo(() => {
        // Use selected employee if available (for admin view), otherwise use logged-in user
        const targetUser = employee || user;
        if (!targetUser) return false;

        // Check if user has groups array
        if (Array.isArray(targetUser.groups)) {
            return targetUser.groups.some((group) => {
                // Handle both string format and object format
                if (typeof group === "string") {
                    return group === "APUS";
                }
                // Handle object format with name property
                return group.name === "APUS";
            });
        }

        // Check if user has group object (older format)
        if (targetUser.group && targetUser.group.name) {
            return targetUser.group.name === "APUS";
        }

        return false;
    }, [user, employee]);

    // Function to get the appropriate per diem rate
    const getPerDiemRate = React.useMemo(() => {
        const currentYear = new Date().getFullYear();
        const rates = perDiemRates;

        // Find current rates for the current year
        const currentRates = rates.filter(
            (rate) => rate.year === currentYear && rate.is_current
        );

        if (selectedEmployeeHasApus) {
            // Look for APUS rate first
            const apusRate = currentRates.find((rate) => rate.is_apus);
            if (apusRate) {
                return parseFloat(apusRate.amount);
            }
        }

        // Fall back to regular rate
        const regularRate = currentRates.find((rate) => !rate.is_apus);
        if (regularRate) {
            return parseFloat(regularRate.amount);
        }

        // Final fallback to hardcoded value if no rates found
        return 45;
    }, [selectedEmployeeHasApus, perDiemRates]);

    const perDiemRate = getPerDiemRate;
    const ISO_WEEK_CONTROL_HEIGHT = 48;

    const derivedIsoWeekFromExpense = React.useMemo(() => {
        if (editing && expense && expense.start_date) {
            const parsedStart = parseDate(expense.start_date);
            if (parsedStart) {
                return moment(parsedStart).format("GGGG[W]WW");
            }
        }
        return null;
    }, [editing, expense]);

    const normalizedIsoWeek = React.useMemo(() => {
        if (derivedIsoWeekFromExpense) {
            return derivedIsoWeekFromExpense;
        }
        if (isoWeek && moment(isoWeek, "GGGG[W]WW", true).isValid()) {
            return isoWeek;
        }
        return moment().format("GGGG[W]WW");
    }, [derivedIsoWeekFromExpense, isoWeek]);

    const [selectedIsoWeek, setSelectedIsoWeek] = React.useState(
        normalizedIsoWeek
    );

    React.useEffect(() => {
        setSelectedIsoWeek((prev) => {
            if (prev === normalizedIsoWeek) {
                return prev;
            }
            return normalizedIsoWeek;
        });
    }, [normalizedIsoWeek]);

    const isoWeekMoment = React.useMemo(() => {
        if (!selectedIsoWeek) return null;
        const parsed = moment(selectedIsoWeek, "GGGG[W]WW", true);
        return parsed.isValid() ? parsed : null;
    }, [selectedIsoWeek]);

    const weekStartDate = React.useMemo(() => {
        if (!isoWeekMoment) return null;
        return isoWeekMoment
            .clone()
            .startOf("isoWeek")
            .toDate();
    }, [isoWeekMoment]);

    const weekEndDate = React.useMemo(() => {
        if (!isoWeekMoment) return null;
        return isoWeekMoment
            .clone()
            .startOf("isoWeek")
            .add(6, "days")
            .toDate();
    }, [isoWeekMoment]);

    const isoWeekSummary = React.useMemo(() => {
        if (!weekStartDate || !weekEndDate) return null;
        const baseMoment = isoWeekMoment
            ? isoWeekMoment
            : moment(weekStartDate);
        const weekNumber = baseMoment.isoWeek();
        const year = baseMoment.isoWeekYear();
        const weekLabel = `Week ${year}W${String(weekNumber).padStart(2, "0")}`;
        const startLabel = moment(weekStartDate).format("MMM D");
        const endLabel = moment(weekEndDate).format("MMM D");
        return {
            weekLabel,
            startLabel,
            endLabel,
        };
    }, [weekStartDate, weekEndDate, isoWeekMoment]);

    const isoWeekInfo = React.useMemo(() => {
        if (isoWeekMoment) {
            return {
                weekNumber: isoWeekMoment.isoWeek(),
            };
        }
        if (values.start_date) {
            const startMoment = moment(values.start_date);
            if (startMoment.isValid()) {
                return {
                    weekNumber: startMoment.isoWeek(),
                };
            }
        }

        return {
            weekNumber: null,
        };
    }, [isoWeekMoment, values.start_date]);

    React.useEffect(() => {
        if (!weekEndDate) return;
        setValues((prev) => {
            if (!prev.start_date) return prev;
            const weekEndMoment = moment(weekEndDate).startOf("day");
            const maxStart = weekEndMoment.clone().subtract(1, "day");
            const currentStart = moment(prev.start_date).startOf("day");

            if (currentStart.isAfter(maxStart)) {
                const adjustedStartMoment = maxStart.clone();
                const adjustedStart = adjustedStartMoment.toDate();
                const minEnd = adjustedStartMoment.clone().add(1, "day");
                const currentEnd = prev.end_date
                    ? moment(prev.end_date).startOf("day")
                    : null;

                const nextValues = {
                    ...prev,
                    start_date: adjustedStart,
                };

                if (
                    !currentEnd ||
                    currentEnd.isSameOrBefore(adjustedStartMoment) ||
                    currentEnd.isAfter(weekEndMoment)
                ) {
                    const resolvedEnd = moment.min(minEnd, weekEndMoment);
                    nextValues.end_date = resolvedEnd.toDate();
                }

                return nextValues;
            }

            return prev;
        });
    }, [weekEndDate]);

    const isSalesUser = React.useMemo(() => {
        return Array.isArray(user && user.groups)
            ? user.groups.some((group) => group.name === "SALES")
            : false;
    }, [user]);

    const isManagementUser = React.useMemo(() => {
        return Array.isArray(user && user.groups)
            ? user.groups.some((group) => group.name === "MANAGEMENT")
            : false;
    }, [user]);

    React.useEffect(() => {
        if (menuOptions.length === 0) return;
        const maxIndex = menuOptions.length - 1;
        if (menuSelection > maxIndex) {
            setMenuSelection(maxIndex);
        }
    }, [menuOptions, menuSelection]);

    React.useEffect(() => {
        if (didMount.current) {
            if (editing && expense) {
                if (expense.service !== null) {
                    setMenuSelection(1);
                }
                if (expense.hse !== null) {
                    setMenuSelection(2);
                }
                if (expense.quote !== null) {
                    setMenuSelection(3);
                }
                if (expense.project !== null) {
                    setMenuSelection(0);
                }
            }
        } else {
            didMount.current = true;
        }
    }, [editing, expense]);

    React.useEffect(() => {
        if (!editing) {
            setValues((prev) => ({
                ...prev,
                hse: "",
                project: "",
                service: "",
                quote: "",
            }));
        }
    }, [menuSelection, editing]);

    const initialFormValues = React.useMemo(() => {
        const startMoment = weekStartDate
            ? moment(weekStartDate).startOf("day")
            : moment().startOf("day");
        const tentativeEnd = startMoment.clone().add(1, "day");
        const cappedEnd = weekEndDate
            ? moment.min(tentativeEnd, moment(weekEndDate).startOf("day"))
            : tentativeEnd;

        return {
            project: "",
            service: "",
            hse: "",
            quote: "",
            start_date: startMoment.toDate(),
            end_date: cappedEnd.toDate(),
            rate: perDiemRate,
            notes: "",
        };
    }, [perDiemRate, weekStartDate, weekEndDate]);

    const editFormValues = React.useMemo(() => {
        const currentExpense = expense || {};
        const resolveRelation = (key) => {
            const value = currentExpense[key];
            if (!editing) return value;
            if (value && typeof value === "object" && value.id !== undefined) {
                return value.id;
            }
            return value;
        };

        const parsedStart = parseDate(currentExpense.start_date) || new Date();
        const parsedEnd =
            parseDate(currentExpense.end_date) ||
            moment(parsedStart)
                .add(1, "day")
                .toDate();

        return {
            quote: resolveRelation("quote"),
            project: resolveRelation("project"),
            service: resolveRelation("service"),
            hse: resolveRelation("hse"),
            start_date: parsedStart,
            end_date: parsedEnd,
            rate: resolveRelation("rate") || perDiemRate,
            notes: currentExpense.notes ? currentExpense.notes : "",
        };
    }, [editing, expense, perDiemRate]);

    React.useLayoutEffect(() => {
        if (!openPerDiem) return;
        setValues(editing ? editFormValues : initialFormValues);
    }, [openPerDiem, editing, editFormValues, initialFormValues]);

    // Update rate when employee changes (for admin view)
    React.useEffect(() => {
        if (!openPerDiem) return;

        // Debug logging
        // console.log("Employee changed:", employee);
        // console.log("Selected employee has APUS:", selectedEmployeeHasApus);
        // console.log("New per diem rate:", perDiemRate);

        setValues((prev) => ({
            ...prev,
            rate: perDiemRate,
        }));
    }, [employee, perDiemRate, openPerDiem, selectedEmployeeHasApus]);

    React.useEffect(() => {
        if (!weekStartDate || !weekEndDate) return;
        setValues((prev) => {
            const minStartMoment = moment(weekStartDate).startOf("day");
            const rawMaxStart = moment(weekEndDate)
                .startOf("day")
                .subtract(1, "day");
            const maxStartMoment = rawMaxStart.isBefore(minStartMoment)
                ? minStartMoment.clone()
                : rawMaxStart.clone();

            let nextStartMoment = prev.start_date
                ? moment(prev.start_date).startOf("day")
                : null;
            let startChanged = false;

            if (!nextStartMoment) {
                nextStartMoment = minStartMoment.clone();
                startChanged = true;
            } else {
                if (nextStartMoment.isBefore(minStartMoment)) {
                    nextStartMoment = minStartMoment.clone();
                    startChanged = true;
                }
                if (nextStartMoment.isAfter(maxStartMoment)) {
                    nextStartMoment = maxStartMoment.clone();
                    startChanged = true;
                }
            }

            const weekEndMoment = moment(weekEndDate).startOf("day");
            const minEndMoment = nextStartMoment.clone().add(1, "day");
            const currentEndMoment = prev.end_date
                ? moment(prev.end_date).startOf("day")
                : null;

            let nextEndMoment = currentEndMoment
                ? currentEndMoment.clone()
                : null;
            let endChanged = false;

            if (!currentEndMoment) {
                nextEndMoment = moment.min(minEndMoment, weekEndMoment);
                endChanged = true;
            } else {
                if (
                    currentEndMoment.isSame(nextStartMoment) ||
                    currentEndMoment.isBefore(nextStartMoment)
                ) {
                    nextEndMoment = moment.min(minEndMoment, weekEndMoment);
                    endChanged = true;
                }
                if (currentEndMoment.isAfter(weekEndMoment)) {
                    nextEndMoment = weekEndMoment.clone();
                    endChanged = true;
                }
                if (minEndMoment.isAfter(weekEndMoment)) {
                    nextEndMoment = weekEndMoment.clone();
                    endChanged = true;
                }
            }

            if (!startChanged && !endChanged) {
                return prev;
            }

            const nextValues = {
                ...prev,
            };

            if (startChanged) {
                nextValues.start_date = nextStartMoment.toDate();
                const newIsoWeekValue = nextStartMoment.format("GGGG[W]WW");
                if (newIsoWeekValue !== selectedIsoWeek) {
                    setSelectedIsoWeek(newIsoWeekValue);
                    if (typeof onIsoWeekChange === "function") {
                        onIsoWeekChange(newIsoWeekValue);
                    }
                }
            }

            if (nextEndMoment) {
                nextValues.end_date = nextEndMoment.toDate();
            }

            return nextValues;
        });
    }, [weekStartDate, weekEndDate, onIsoWeekChange, selectedIsoWeek]);

    const handleInputValue = (event) => {
        const { name, value } = event.target;
        setValues((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleChangeProject = (newValue) => {
        switch (menuSelection) {
            case 1:
                if (newValue) {
                    setValues((prev) => ({
                        ...prev,
                        service: newValue.id,
                        project: null,
                        hse: null,
                        quote: null,
                    }));
                }
                break;
            case 2:
                if (newValue) {
                    setValues((prev) => ({
                        ...prev,
                        hse: newValue.id,
                        project: null,
                        service: null,
                        quote: null,
                    }));
                }
                break;
            case 3:
                if (newValue) {
                    setValues((prev) => ({
                        ...prev,
                        quote: newValue.id,
                        project: null,
                        hse: null,
                        service: null,
                    }));
                }
                break;
            default:
                if (newValue) {
                    setValues((prev) => ({
                        ...prev,
                        project: newValue.id,
                        service: null,
                        hse: null,
                        quote: null,
                    }));
                }
        }
    };

    const clampToWeek = React.useCallback(
        (inputDate) => {
            if (!inputDate) return inputDate;
            let next = moment(inputDate).startOf("day");
            if (weekStartDate) {
                const startBoundary = moment(weekStartDate).startOf("day");
                if (next.isBefore(startBoundary)) {
                    next = startBoundary;
                }
            }
            if (weekEndDate) {
                const endBoundary = moment(weekEndDate).startOf("day");
                if (next.isAfter(endBoundary)) {
                    next = endBoundary;
                }
            }
            return next.toDate();
        },
        [weekStartDate, weekEndDate]
    );

    const handleDateChange = (field) => (date) => {
        if (!date) return;
        let clamped = clampToWeek(date);
        if (field === "start_date" && clamped && weekEndDate) {
            const maxStart = moment(weekEndDate)
                .startOf("day")
                .subtract(1, "day");
            const candidate = moment(clamped).startOf("day");
            const minStart = weekStartDate
                ? moment(weekStartDate).startOf("day")
                : null;

            if (candidate.isAfter(maxStart)) {
                clamped = maxStart.toDate();
            }
            if (minStart && minStart.isAfter(maxStart)) {
                clamped = minStart.toDate();
            }
        }
        if (field === "start_date" && clamped) {
            const newIsoWeekValue = moment(clamped).format("GGGG[W]WW");
            if (newIsoWeekValue !== selectedIsoWeek) {
                setSelectedIsoWeek(newIsoWeekValue);
                if (typeof onIsoWeekChange === "function") {
                    onIsoWeekChange(newIsoWeekValue);
                }
            }
        }
        setValues((prev) => {
            const updated = {
                ...prev,
                [field]: clamped,
            };
            const startMoment = updated.start_date
                ? moment(updated.start_date).startOf("day")
                : null;
            const endMoment = updated.end_date
                ? moment(updated.end_date).startOf("day")
                : null;

            if (field === "start_date" && startMoment) {
                const minEnd = startMoment.clone().add(1, "day");
                const maxEnd = weekEndDate
                    ? moment(weekEndDate).startOf("day")
                    : null;
                let resolvedEnd = minEnd;

                if (maxEnd) {
                    if (minEnd.isAfter(maxEnd)) {
                        resolvedEnd = null;
                    } else {
                        resolvedEnd = moment.min(minEnd, maxEnd);
                    }
                }

                if (
                    !endMoment ||
                    endMoment.isSame(startMoment) ||
                    endMoment.isBefore(startMoment) ||
                    (maxEnd && endMoment.isAfter(maxEnd))
                ) {
                    updated.end_date = resolvedEnd
                        ? resolvedEnd.toDate()
                        : null;
                }
            }

            if (field === "end_date" && endMoment && startMoment) {
                const minEnd = startMoment.clone().add(1, "day");
                const maxEnd = weekEndDate
                    ? moment(weekEndDate).startOf("day")
                    : null;
                let nextEndMoment = endMoment.clone();

                if (maxEnd && nextEndMoment.isAfter(maxEnd)) {
                    nextEndMoment = maxEnd.clone();
                }

                if (
                    nextEndMoment.isSame(startMoment) ||
                    nextEndMoment.isBefore(startMoment)
                ) {
                    if (maxEnd && minEnd.isAfter(maxEnd)) {
                        updated.end_date = null;
                    } else {
                        const adjustedEnd = maxEnd
                            ? moment.min(minEnd, maxEnd)
                            : minEnd;
                        updated.end_date = adjustedEnd.toDate();
                    }
                } else {
                    updated.end_date = nextEndMoment.toDate();
                }
            }
            return updated;
        });
    };

    const adjustDate = React.useCallback(
        (field, delta) => {
            const currentValue = values[field];
            if (!currentValue || !delta) return;
            const currentMoment = moment(currentValue).startOf("day");
            const nextMoment = currentMoment.clone().add(delta, "day");
            if (nextMoment.isSame(currentMoment)) return;
            const handler = handleDateChange(field);
            handler(nextMoment.toDate());
        },
        [values, handleDateChange]
    );

    const handleIsoWeekSelect = React.useCallback(
        (newIsoWeek) => {
            if (!newIsoWeek) return;
            setSelectedIsoWeek(newIsoWeek);
            if (typeof onIsoWeekChange === "function") {
                onIsoWeekChange(newIsoWeek);
            }
        },
        [onIsoWeekChange]
    );

    const handleMoveIsoWeek = React.useCallback(
        (direction) => {
            if (typeof direction !== "number" || Number.isNaN(direction)) {
                return;
            }
            setSelectedIsoWeek((prev) => {
                const base = moment(prev, "GGGG[W]WW", true);
                const safe = base.isValid() ? base : moment();
                const next = safe.add(direction, "week").format("GGGG[W]WW");
                if (typeof onIsoWeekChange === "function") {
                    onIsoWeekChange(next);
                }
                return next;
            });
        },
        [onIsoWeekChange]
    );

    const handleSubmit = () => {
        // Find the appropriate PerDiemRate object ID based on current conditions
        const getCurrentPerDiemRateId = () => {
            const currentYear = new Date().getFullYear();
            const rates = perDiemRates;

            // Find current rates for the current year
            const currentRates = rates.filter(
                (rate) => rate.year === currentYear && rate.is_current
            );

            if (selectedEmployeeHasApus) {
                const apusRate = currentRates.find((rate) => rate.is_apus);
                if (apusRate) {
                    return apusRate.id;
                }
            }

            // Fall back to regular rate
            const regularRate = currentRates.find((rate) => !rate.is_apus);
            if (regularRate) {
                return regularRate.id;
            }

            // If no rates found, return null
            return null;
        };

        const rateId = getCurrentPerDiemRateId();
        const startDate = values.start_date
            ? moment
                  .tz(values.start_date, TZ)
                  ._d.toISOString()
                  .split("T")[0]
            : null;
        const endDate = values.end_date
            ? moment
                  .tz(values.end_date, TZ)
                  ._d.toISOString()
                  .split("T")[0]
            : null;

        const data = {
            quote: values.quote || null,
            project: values.project || null,
            service: values.service || null,
            hse: values.hse || null,
            start_date: startDate,
            end_date: endDate,
            rate: rateId,
            notes: values.notes || "",
            is_approved: false,
            user: employee ? employee.id : null,
        };

        if (editing && updatePerDiem) {
            const perDiemId = expense ? expense.id : undefined;
            updatePerDiem(perDiemId, data);
            setOpenPerDiem(false);
        } else if (createPerDiem) {
            // console.log(
            //     "Creating PerDiem with data:",
            //     JSON.stringify(data, null, 2)
            // );
            // console.log("Employee:", JSON.stringify(employee, null, 2));
            // console.log("User:", JSON.stringify(user, null, 2));
            // console.log(
            //     "Employee ID being sent:",
            //     employee ? employee.id : null
            // );
            createPerDiem(data);
            setOpenPerDiem(false);
        }
        // console.log("Final data object:", JSON.stringify(data, null, 2));
    };

    const handleValidation = () => {
        let formIsValid = true;
        // Find the appropriate PerDiemRate object ID for validation
        const getCurrentPerDiemRateId = () => {
            const currentYear = new Date().getFullYear();
            const rates = perDiemRates;

            // Find current rates for the current year
            const currentRates = rates.filter(
                (rate) => rate.year === currentYear && rate.is_current
            );

            if (selectedEmployeeHasApus) {
                const apusRate = currentRates.find((rate) => rate.is_apus);
                if (apusRate) {
                    return apusRate.id;
                }
            }

            // Fall back to regular rate
            const regularRate = currentRates.find((rate) => !rate.is_apus);
            if (regularRate) {
                return regularRate.id;
            }

            return null;
        };

        const rateId = getCurrentPerDiemRateId();

        if (
            values.project === "" &&
            values.service === "" &&
            values.hse === "" &&
            values.quote === ""
        ) {
            setErrors((prev) => ({ ...prev, project: "Required field" }));
            formIsValid = false;
            setTimeout(() => {
                setErrors((prev) => ({ ...prev, project: null }));
            }, 3000);
        } else if (!values.start_date) {
            setErrors((prev) => ({ ...prev, start_date: "Select start date" }));
            formIsValid = false;
            setTimeout(() => {
                setErrors((prev) => ({ ...prev, start_date: null }));
            }, 3000);
        } else if (!values.end_date) {
            setErrors((prev) => ({ ...prev, end_date: "Select end date" }));
            formIsValid = false;
            setTimeout(() => {
                setErrors((prev) => ({ ...prev, end_date: null }));
            }, 3000);
        } else if (
            values.start_date &&
            values.end_date &&
            moment(values.end_date)
                .startOf("day")
                .isBefore(moment(values.start_date).startOf("day"))
        ) {
            setErrors((prev) => ({
                ...prev,
                end_date: "End date cannot be before start date",
            }));
            formIsValid = false;
            setTimeout(() => {
                setErrors((prev) => ({ ...prev, end_date: null }));
            }, 3000);
        } else if (
            values.start_date &&
            values.end_date &&
            moment(values.end_date)
                .startOf("day")
                .isSame(moment(values.start_date).startOf("day"))
        ) {
            setErrors((prev) => ({
                ...prev,
                end_date: "End date must be after start date",
            }));
            formIsValid = false;
            setTimeout(() => {
                setErrors((prev) => ({ ...prev, end_date: null }));
            }, 3000);
        } else if (!rateId) {
            setErrors((prev) => ({ ...prev, rate: "Select rate" }));
            formIsValid = false;
            setTimeout(() => {
                setErrors((prev) => ({ ...prev, rate: null }));
            }, 3000);
        } else if (isManagementUser && !employee) {
            setErrors((prev) => ({ ...prev, employee: "Select Employee" }));
            formIsValid = false;
            setTimeout(() => {
                setErrors((prev) => ({ ...prev, employee: null }));
            }, 3000);
        } else {
            setErrors({
                project: null,
                start_date: null,
                end_date: null,
                rate: null,
                employee: null,
            });
            formIsValid = true;
        }

        setIsValid(formIsValid);
        setTimeout(() => {
            setIsValid(true);
        }, 3000);

        return formIsValid ? handleSubmit() : null;
    };

    const handleClose = () => {
        setOpenPerDiem(false);
    };

    let picker = <div></div>;

    switch (menuSelection) {
        case 1:
            picker = (
                <ServicePicker
                    token={token}
                    handleChangeProject={handleChangeProject}
                    editing={editing}
                    editObject={expense}
                    errors={errors}
                    editProject={values.project}
                />
            );
            break;
        case 2:
            picker = (
                <HSEPicker
                    token={token}
                    handleChangeProject={handleChangeProject}
                    editing={editing}
                    editObject={expense}
                    errors={errors}
                    editProject={values.project}
                />
            );
            break;
        case 3:
            picker = (
                <QuotePicker
                    token={token}
                    handleChangeQuote={handleChangeProject}
                    errors={errors}
                    editing={editing}
                    editObject={expense}
                />
            );
            break;
        default:
            picker = (
                <ProjectPicker
                    token={token}
                    handleChangeProject={handleChangeProject}
                    editing={editing}
                    editObject={expense}
                    errors={errors}
                    editProject={values.project}
                />
            );
    }

    const nights = calcNights(values.start_date, values.end_date);
    const estimatedTotal = nights * perDiemRate;

    const minSelectableEndDate = React.useMemo(() => {
        if (values.start_date) {
            const minDate = moment(values.start_date)
                .startOf("day")
                .add(1, "day");
            if (weekEndDate) {
                const weekEnd = moment(weekEndDate).startOf("day");
                if (minDate.isAfter(weekEnd)) {
                    return weekEnd.toDate();
                }
            }
            return minDate.toDate();
        }
        return weekStartDate || undefined;
    }, [values.start_date, weekEndDate, weekStartDate]);

    const maxSelectableStartDate = React.useMemo(() => {
        if (weekEndDate) {
            const maxStart = moment(weekEndDate)
                .startOf("day")
                .subtract(1, "day");
            if (weekStartDate) {
                const weekStart = moment(weekStartDate).startOf("day");
                if (maxStart.isBefore(weekStart)) {
                    return weekStart.toDate();
                }
            }
            return maxStart.toDate();
        }
        return weekStartDate || undefined;
    }, [weekEndDate, weekStartDate]);

    const shouldDisableEndDate = React.useCallback(
        (date) => {
            if (!date) return false;
            const candidate = moment(date).startOf("day");

            if (values.start_date) {
                const startMoment = moment(values.start_date).startOf("day");
                if (
                    candidate.isSame(startMoment) ||
                    candidate.isBefore(startMoment)
                ) {
                    return true;
                }
            }

            if (weekStartDate) {
                const weekStart = moment(weekStartDate).startOf("day");
                if (candidate.isBefore(weekStart)) {
                    return true;
                }
            }

            if (weekEndDate) {
                const weekEnd = moment(weekEndDate).startOf("day");
                if (candidate.isAfter(weekEnd)) {
                    return true;
                }
            }

            return false;
        },
        [values.start_date, weekEndDate, weekStartDate]
    );

    const shouldDisableStartDate = React.useCallback(
        (date) => {
            if (!date) return false;
            const candidate = moment(date).startOf("day");

            if (weekStartDate) {
                const weekStart = moment(weekStartDate).startOf("day");
                if (candidate.isBefore(weekStart)) {
                    return true;
                }
            }

            if (weekEndDate) {
                const maxStart = moment(weekEndDate)
                    .startOf("day")
                    .subtract(1, "day");
                if (candidate.isAfter(maxStart)) {
                    return true;
                }
            }

            return false;
        },
        [weekStartDate, weekEndDate]
    );

    const canIncrementStart = React.useMemo(() => {
        if (!values.start_date) return false;
        if (!maxSelectableStartDate) return true;
        const current = moment(values.start_date).startOf("day");
        const maxStart = moment(maxSelectableStartDate).startOf("day");
        return current.isBefore(maxStart);
    }, [values.start_date, maxSelectableStartDate]);

    const canDecrementStart = React.useMemo(() => {
        if (!values.start_date) return false;
        if (!weekStartDate) return true;
        const current = moment(values.start_date).startOf("day");
        const minStart = moment(weekStartDate).startOf("day");
        return current.isAfter(minStart);
    }, [values.start_date, weekStartDate]);

    const canIncrementEnd = React.useMemo(() => {
        if (!values.end_date) return false;
        if (!weekEndDate) return true;
        const current = moment(values.end_date).startOf("day");
        const maxEnd = moment(weekEndDate).startOf("day");
        return current.isBefore(maxEnd);
    }, [values.end_date, weekEndDate]);

    const canDecrementEnd = React.useMemo(() => {
        if (!values.end_date || !values.start_date) return false;
        const currentEnd = moment(values.end_date).startOf("day");
        const minEnd = moment(values.start_date)
            .startOf("day")
            .add(1, "day");
        return currentEnd.isAfter(minEnd);
    }, [values.end_date, values.start_date]);

    const handleStartIncrement = React.useCallback(
        () => adjustDate("start_date", 1),
        [adjustDate]
    );

    const handleStartDecrement = React.useCallback(
        () => adjustDate("start_date", -1),
        [adjustDate]
    );

    const handleEndIncrement = React.useCallback(
        () => adjustDate("end_date", 1),
        [adjustDate]
    );

    const handleEndDecrement = React.useCallback(
        () => adjustDate("end_date", -1),
        [adjustDate]
    );

    return (
        <div>
            <Dialog
                TransitionComponent={Transition}
                fullWidth
                fullScreen
                open={openPerDiem}
                onClose={handleClose}
            >
                <DialogTitle>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                        }}
                    >
                        <div>
                            {`${editing ? "Edit" : "Add"} Per Diem`}
                            {perDiemRate !== null
                                ? ` | $${perDiemRate.toFixed(2)} per night${
                                      selectedEmployeeHasApus ? " (APUS)" : ""
                                  }`
                                : ""}
                        </div>
                        <div>
                            <IconButton
                                edge="end"
                                aria-label="close"
                                onClick={handleClose}
                            >
                                <CloseIcon />
                            </IconButton>
                        </div>
                    </div>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <Stack direction="column" spacing={2}>
                        {values.start_date && values.end_date ? (
                            <Box>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    justifyContent="flex-start"
                                    sx={{
                                        fontWeight: 700,
                                        marginBottom: ".25rem",
                                        width: "100%",
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <Stack spacing={0.5} alignItems="center">
                                        <IsoWeekIcon
                                            weekNumber={isoWeekInfo.weekNumber}
                                        />
                                        <Typography
                                            variant="caption"
                                            component="div"
                                            color="text.secondary"
                                        >
                                            ISO
                                        </Typography>
                                    </Stack>
                                    <Divider orientation="vertical" flexItem />
                                    <Stack
                                        direction="row"
                                        alignItems="flex-start"
                                        spacing={0}
                                    >
                                        {/* Start */}
                                        <Stack
                                            spacing={0.5}
                                            alignItems="center"
                                        >
                                            <CalendarDateIcon
                                                date={values.start_date}
                                            />
                                            <Typography
                                                variant="caption"
                                                component="div"
                                                color="text.secondary"
                                            >
                                                Start
                                            </Typography>
                                        </Stack>

                                        {/* Arrow (tight, centered only on icons) */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center", // center on icon height
                                                px: 0, // no horizontal padding
                                                mx: 0, // no extra margin
                                                marginTop: "7px", // nudge down to center with text
                                            }}
                                        >
                                            <ArrowRightIcon
                                                sx={{
                                                    display: "block",
                                                    fontSize: {
                                                        xs: "1.5rem",
                                                        sm: "1.5rem",
                                                    },
                                                    color: "text.secondary",
                                                    m: 0,
                                                }}
                                            />
                                        </Box>

                                        {/* End */}
                                        <Stack
                                            spacing={0.5}
                                            alignItems="center"
                                        >
                                            <CalendarDateIcon
                                                date={values.end_date}
                                            />
                                            <Typography
                                                variant="caption"
                                                component="div"
                                                color="text.secondary"
                                            >
                                                End
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                    <Divider orientation="vertical" flexItem />
                                    <Stack spacing={0.5} alignItems="center">
                                        <NightsStayedIcon nights={nights} />
                                        <Typography
                                            variant="caption"
                                            component="div"
                                            color="text.secondary"
                                        >
                                            {nights === 1 ? "Night" : "Nights"}
                                        </Typography>
                                    </Stack>
                                    <Divider orientation="vertical" flexItem />
                                    <Stack spacing={0.5} alignItems="center">
                                        <TotalCostIcon
                                            amount={estimatedTotal}
                                        />
                                        <Typography
                                            variant="caption"
                                            component="div"
                                            color="text.secondary"
                                        >
                                            Total
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Box>
                        ) : null}
                        <Divider
                            sx={{ borderWidth: 1, borderColor: "success.main" }}
                        />
                        {isManagementUser ? (
                            <EmployeePicker
                                editing={editing}
                                editObject={expense}
                                employee={employee}
                                errors={errors}
                                user={user}
                                token={token}
                                handleChangeEmployee={handleChangeEmployee}
                                employees={employees}
                            />
                        ) : (
                            <></>
                        )}
                        <Stack direction="row" spacing={1}>
                            {picker}
                            <ProjectTypeDropdown
                                user={user}
                                menuOptions={menuOptions}
                                menuSelection={menuSelection}
                                setMenuSelection={setMenuSelection}
                            />
                        </Stack>
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ flexWrap: "wrap" }}
                        >
                            <Box
                                sx={{
                                    "& .MuiButton-root": {
                                        height: ISO_WEEK_CONTROL_HEIGHT,
                                    },
                                }}
                            >
                                <WeekPicker
                                    isoWeek={selectedIsoWeek}
                                    getIsoWeek={handleIsoWeekSelect}
                                />
                            </Box>
                            <IconButton
                                color="primary"
                                aria-label="previous iso week"
                                onClick={() => handleMoveIsoWeek(-1)}
                                sx={{
                                    height: ISO_WEEK_CONTROL_HEIGHT,
                                    width: ISO_WEEK_CONTROL_HEIGHT,
                                }}
                            >
                                <ArrowBackIosNewIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                color="primary"
                                aria-label="next iso week"
                                onClick={() => handleMoveIsoWeek(1)}
                                sx={{
                                    height: ISO_WEEK_CONTROL_HEIGHT,
                                    width: ISO_WEEK_CONTROL_HEIGHT,
                                }}
                            >
                                <ArrowForwardIosIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="Start Date"
                                    id="start_date"
                                    name="start_date"
                                    value={values.start_date}
                                    minDate={weekStartDate || undefined}
                                    maxDate={maxSelectableStartDate}
                                    onChange={handleDateChange("start_date")}
                                    shouldDisableDate={shouldDisableStartDate}
                                    renderInput={(params) => {
                                        const existingAdornment =
                                            (params.InputProps &&
                                                params.InputProps
                                                    .endAdornment) ||
                                            null;
                                        return (
                                            <TextField
                                                {...params}
                                                helperText={
                                                    errors.start_date === null
                                                        ? ""
                                                        : errors.start_date
                                                }
                                                error={Boolean(
                                                    errors.start_date
                                                )}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <Stack
                                                            direction="row"
                                                            spacing={0}
                                                            alignItems="center"
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        "flex",
                                                                    flexDirection:
                                                                        "column",
                                                                    border:
                                                                        "1px solid",
                                                                    borderColor:
                                                                        "divider",
                                                                    borderRadius: 1,
                                                                    overflow:
                                                                        "hidden",
                                                                    ml: 0.5,
                                                                    bgcolor:
                                                                        "background.paper",
                                                                }}
                                                            >
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={
                                                                        !canIncrementStart
                                                                    }
                                                                    onClick={(
                                                                        event
                                                                    ) => {
                                                                        event.preventDefault();
                                                                        event.stopPropagation();
                                                                        handleStartIncrement();
                                                                    }}
                                                                    sx={{
                                                                        width: 26,
                                                                        height: 20,
                                                                        borderRadius: 0,
                                                                        borderBottom:
                                                                            "1px solid",
                                                                        borderColor:
                                                                            "divider",
                                                                        p: 0,
                                                                    }}
                                                                >
                                                                    <KeyboardArrowUpIcon
                                                                        sx={{
                                                                            fontSize:
                                                                                "1rem",
                                                                        }}
                                                                    />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={
                                                                        !canDecrementStart
                                                                    }
                                                                    onClick={(
                                                                        event
                                                                    ) => {
                                                                        event.preventDefault();
                                                                        event.stopPropagation();
                                                                        handleStartDecrement();
                                                                    }}
                                                                    sx={{
                                                                        width: 26,
                                                                        height: 20,
                                                                        borderRadius: 0,
                                                                        p: 0,
                                                                    }}
                                                                >
                                                                    <KeyboardArrowDownIcon
                                                                        sx={{
                                                                            fontSize:
                                                                                "1rem",
                                                                        }}
                                                                    />
                                                                </IconButton>
                                                            </Box>
                                                            {existingAdornment}
                                                        </Stack>
                                                    ),
                                                }}
                                                fullWidth
                                            />
                                        );
                                    }}
                                    fullWidth
                                />
                            </LocalizationProvider>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="End Date"
                                    id="end_date"
                                    name="end_date"
                                    value={values.end_date}
                                    minDate={minSelectableEndDate}
                                    maxDate={weekEndDate || undefined}
                                    onChange={handleDateChange("end_date")}
                                    shouldDisableDate={shouldDisableEndDate}
                                    renderInput={(params) => {
                                        const existingAdornment =
                                            (params.InputProps &&
                                                params.InputProps
                                                    .endAdornment) ||
                                            null;
                                        return (
                                            <TextField
                                                {...params}
                                                helperText={
                                                    errors.end_date === null
                                                        ? ""
                                                        : errors.end_date
                                                }
                                                error={Boolean(errors.end_date)}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <Stack
                                                            direction="row"
                                                            spacing={0}
                                                            alignItems="center"
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        "flex",
                                                                    flexDirection:
                                                                        "column",
                                                                    border:
                                                                        "1px solid",
                                                                    borderColor:
                                                                        "divider",
                                                                    borderRadius: 1,
                                                                    overflow:
                                                                        "hidden",
                                                                    ml: 0.5,
                                                                    bgcolor:
                                                                        "background.paper",
                                                                }}
                                                            >
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={
                                                                        !canIncrementEnd
                                                                    }
                                                                    onClick={(
                                                                        event
                                                                    ) => {
                                                                        event.preventDefault();
                                                                        event.stopPropagation();
                                                                        handleEndIncrement();
                                                                    }}
                                                                    sx={{
                                                                        width: 26,
                                                                        height: 20,
                                                                        borderRadius: 0,
                                                                        borderBottom:
                                                                            "1px solid",
                                                                        borderColor:
                                                                            "divider",
                                                                        p: 0,
                                                                    }}
                                                                >
                                                                    <KeyboardArrowUpIcon
                                                                        sx={{
                                                                            fontSize:
                                                                                "1rem",
                                                                        }}
                                                                    />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={
                                                                        !canDecrementEnd
                                                                    }
                                                                    onClick={(
                                                                        event
                                                                    ) => {
                                                                        event.preventDefault();
                                                                        event.stopPropagation();
                                                                        handleEndDecrement();
                                                                    }}
                                                                    sx={{
                                                                        width: 26,
                                                                        height: 20,
                                                                        borderRadius: 0,
                                                                        p: 0,
                                                                    }}
                                                                >
                                                                    <KeyboardArrowDownIcon
                                                                        sx={{
                                                                            fontSize:
                                                                                "1rem",
                                                                        }}
                                                                    />
                                                                </IconButton>
                                                            </Box>
                                                            {existingAdornment}
                                                        </Stack>
                                                    ),
                                                }}
                                                fullWidth
                                            />
                                        );
                                    }}
                                    fullWidth
                                />
                            </LocalizationProvider>
                        </Stack>
                        <TextField
                            autoFocus={false}
                            id="notes"
                            name="notes"
                            label="Notes"
                            onChange={handleInputValue}
                            value={values.notes}
                            multiline
                            rows={4}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleValidation}
                        color={isValid ? "primary" : "error"}
                    >
                        {editing ? "Update" : "Submit"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
